import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const ACTION_COLOR = {
  create: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400",
  update: "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400",
  delete: "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400",
};

export default function ActivityLog() {
  const { isAdmin } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAdmin) { setLoading(false); return; }
    api.get("/activity").then((r) => setLogs(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [isAdmin]);

  if (!isAdmin) return (
    <div className="max-w-md mx-auto mt-16 text-center" data-testid="restricted-notice">
      <div className="h-14 w-14 rounded-2xl bg-destructive/10 text-destructive flex items-center justify-center mx-auto mb-4">
        <Icons.Lock className="h-6 w-6" />
      </div>
      <h2 className="font-display text-xl font-bold">Access Restricted</h2>
      <p className="text-muted-foreground text-sm mt-2">Only Admins can view the audit trail.</p>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <div className="overline text-muted-foreground mb-1">Audit Trail</div>
        <h1 className="font-display text-2xl font-bold tracking-tight">Activity Log</h1>
        <p className="text-sm text-muted-foreground mt-1">Who changed what, and when.</p>
      </div>
      <Card className="divide-y">
        {loading && <div className="p-8 text-center text-muted-foreground">Loading…</div>}
        {!loading && logs.length === 0 && <div className="p-8 text-center text-muted-foreground">No activity recorded yet.</div>}
        {logs.map((a) => (
          <div key={a.id} className="flex items-start gap-3 p-4" data-testid={`activity-${a.id}`}>
            <div className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center shrink-0">
              <Icons.User className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-medium">{a.user_name}</span>
                <Badge variant="secondary" className={`text-[10px] ${ACTION_COLOR[a.action] || ""}`}>{a.action}</Badge>
                <span className="text-muted-foreground">{a.resource}</span>
                {a.record_name && <span className="font-medium truncate">“{a.record_name}”</span>}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">{new Date(a.timestamp).toLocaleString()}</div>
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}
